/**
 * MeetingPrepColumn Component
 * Meeting Prep Feature - Spezielle Spalte für Meeting-Vorbereitung
 * Zeigt Kalendertermine mit zugehörigen Mails
 */

import { useState, useMemo, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  Calendar,
  Clock,
  Users,
  Mail,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Video,
  MapPin,
  Loader2,
  RefreshCw,
  AlertCircle,
  Paperclip,
  Settings,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  useMeetingPreps,
  useSyncCalendar,
  useLinkEmailToEvent,
} from '../../hooks/useCalendar';
import {
  CalendarEvent,
  RelatedEmail,
  getEventCountdown,
  formatEventDuration,
  getAttendeeInitials,
} from '../../api/calendar.api';
import { toast } from 'sonner';
import MeetingPrepSettings, { getStoredSettings } from './MeetingPrepSettings';

// ============ Constants ============

const TIMEZONE = 'Europe/Berlin';

// ============ Types ============

interface MeetingPrepColumnProps {
  column: {
    id: string;
    displayName: string;
    icon?: string;
    frozen?: boolean;
  };
  account: {
    id: string;
    provider: string;
    providerEmail: string;
  } | null;
  onSelectMail?: (messageId: string) => void;
  selectedMessageId?: string;
  isFrozen?: boolean;
  isDragEnabled?: boolean;
  onQuickAction?: (data: { message: any; accountId: string; columnId: string }) => void;
  onAddTodo?: (data: { message: any; accountId: string }) => void;
  onMessagesLoaded?: (messageIds: string[]) => void;
}

interface EventCardProps {
  event: CalendarEvent;
  relatedEmails: RelatedEmail[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSelectMail?: (messageId: string) => void;
  selectedMessageId?: string;
  accountId: string;
}

// ============ Helper Functions ============

/**
 * Format a date with explicit timezone
 */
function formatDateWithTimezone(date: Date, options: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString('de-DE', { ...options, timeZone: TIMEZONE });
}

/**
 * Get the start of day in the target timezone
 */
function getStartOfDayInTimezone(date: Date): Date {
  const dateStr = date.toLocaleDateString('en-CA', { timeZone: TIMEZONE }); // YYYY-MM-DD format
  return new Date(dateStr + 'T00:00:00');
}

/**
 * Get a date key (YYYY-MM-DD) for the given date in the target timezone
 */
function getDateKeyInTimezone(date: Date): string {
  return date.toLocaleDateString('en-CA', { timeZone: TIMEZONE });
}

// ============ Mini Calendar Header ============

function MiniCalendarHeader({
  currentDate,
  onDateChange,
  eventCountsByDay,
}: {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  eventCountsByDay: Map<string, number>;
}) {
  const days = useMemo(() => {
    const result: Date[] = [];
    const now = new Date();
    
    // Get today's date string in target timezone
    const todayStr = getDateKeyInTimezone(now);
    const today = new Date(todayStr + 'T12:00:00'); // Use noon to avoid DST issues

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      result.push(date);
    }

    return result;
  }, []);

  const isSelected = (date: Date) => {
    return getDateKeyInTimezone(date) === getDateKeyInTimezone(currentDate);
  };

  const isToday = (date: Date) => {
    return getDateKeyInTimezone(date) === getDateKeyInTimezone(new Date());
  };

  const getEventCount = (date: Date) => {
    const key = getDateKeyInTimezone(date);
    return eventCountsByDay.get(key) || 0;
  };

  return (
    <div className="flex items-center gap-1 p-2 overflow-x-auto">
      {days.map((date, index) => {
        const count = getEventCount(date);
        const selected = isSelected(date);
        const today = isToday(date);

        return (
          <button
            key={index}
            onClick={() => onDateChange(date)}
            className={cn(
              'flex flex-col items-center min-w-[40px] p-1.5 rounded-lg transition-colors relative',
              selected
                ? 'bg-[var(--brand-primary)] text-white'
                : today
                ? 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]'
                : 'hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]'
            )}
          >
            <span className="text-[10px] font-medium uppercase">
              {formatDateWithTimezone(date, { weekday: 'short' })}
            </span>
            <span className={cn('text-lg font-semibold', selected ? 'text-white' : '')}>
              {date.getDate()}
            </span>
            {count > 0 && (
              <div
                className={cn(
                  'absolute -bottom-0.5 w-1.5 h-1.5 rounded-full',
                  selected ? 'bg-white' : 'bg-[var(--brand-primary)]'
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ============ Attendee Avatar ============

function AttendeeAvatar({
  attendee,
  size = 'sm',
}: {
  attendee: { email: string; name?: string; status?: string };
  size?: 'sm' | 'md';
}) {
  const initials = getAttendeeInitials(attendee as any);
  const sizeClasses = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs';

  const statusColors: Record<string, string> = {
    accepted: 'ring-green-500',
    declined: 'ring-red-500',
    tentative: 'ring-yellow-500',
    needsAction: 'ring-gray-400',
    none: 'ring-gray-300',
  };

  const ringColor = statusColors[attendee.status || 'none'] || statusColors.none;

  return (
    <div
      className={cn(
        sizeClasses,
        'rounded-full flex items-center justify-center font-medium ring-2',
        ringColor,
        'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'
      )}
      title={attendee.name || attendee.email}
    >
      {initials}
    </div>
  );
}

// ============ Countdown Badge ============

function CountdownBadge({ eventStart }: { eventStart: string }) {
  const countdown = getEventCountdown(eventStart);

  const urgencyColors: Record<string, string> = {
    now: 'bg-red-500 text-white animate-pulse',
    soon: 'bg-orange-500 text-white',
    today: 'bg-blue-500 text-white',
    tomorrow: 'bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]',
    thisWeek: 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]',
    later: 'bg-[var(--bg-tertiary)] text-[var(--text-tertiary)]',
  };

  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium', urgencyColors[countdown.urgency])}>
      {countdown.text}
    </span>
  );
}

// ============ Related Email Item ============

function RelatedEmailItem({
  email,
  onSelect,
  isSelected,
}: {
  email: RelatedEmail;
  onSelect?: () => void;
  isSelected?: boolean;
}) {
  return (
    <div
      onClick={onSelect}
      className={cn(
        'flex items-start gap-2 p-2 rounded cursor-pointer transition-colors',
        isSelected ? 'bg-[var(--brand-primary)]/10' : 'hover:bg-[var(--bg-hover)]'
      )}
    >
      <Mail className="w-4 h-4 mt-0.5 text-[var(--text-tertiary)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-xs font-medium truncate',
              !email.isRead ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
            )}
          >
            {email.from.name || email.from.email}
          </span>
          {email.hasAttachment && <Paperclip className="w-3 h-3 text-[var(--text-tertiary)]" />}
        </div>
        <p className="text-xs text-[var(--text-secondary)] truncate">{email.subject}</p>
        <div className="flex items-center gap-1 mt-0.5">
          <span
            className="text-[10px] px-1 py-0.5 rounded"
            style={{
              backgroundColor:
                email.matchType === 'attendee'
                  ? 'rgba(34, 197, 94, 0.1)'
                  : email.matchType === 'subject'
                  ? 'rgba(59, 130, 246, 0.1)'
                  : 'var(--bg-tertiary)',
              color:
                email.matchType === 'attendee'
                  ? 'rgb(34, 197, 94)'
                  : email.matchType === 'subject'
                  ? 'rgb(59, 130, 246)'
                  : 'var(--text-tertiary)',
            }}
          >
            {email.matchType === 'attendee'
              ? 'Teilnehmer'
              : email.matchType === 'subject'
              ? 'Betreff'
              : email.matchType === 'thread'
              ? 'Thread'
              : 'Manuell'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============ Event Card ============

function EventCard({
  event,
  relatedEmails,
  isExpanded,
  onToggleExpand,
  onSelectMail,
  selectedMessageId,
  accountId,
}: EventCardProps) {
  const countdown = getEventCountdown(event.start);
  const duration = formatEventDuration(event.start, event.end, event.isAllDay);
  const displayedAttendees = event.attendees.slice(0, 5);
  const remainingCount = Math.max(0, event.attendees.length - 5);

  return (
    <div
      className={cn(
        'rounded-lg border transition-all',
        countdown.urgency === 'now' || countdown.urgency === 'soon'
          ? 'border-orange-500/50 bg-orange-500/5'
          : 'border-[var(--border-default)] bg-[var(--bg-secondary)]'
      )}
    >
      {/* Event Header */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm text-[var(--text-primary)] truncate">
              {event.title}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <CountdownBadge eventStart={event.start} />
              <span className="text-xs text-[var(--text-tertiary)]">{duration}</span>
            </div>
          </div>

          {/* Meeting Link */}
          {event.meetingLink && (
            <a
              href={event.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-[var(--bg-hover)] text-[var(--brand-primary)]"
              title="Meeting beitreten"
            >
              <Video className="w-4 h-4" />
            </a>
          )}

          {/* External Link */}
          {event.webLink && (
            <a
              href={event.webLink}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]"
              title="Im Kalender öffnen"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Location */}
        {event.location && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-[var(--text-secondary)]">
            <MapPin className="w-3.5 h-3.5" />
            <span className="truncate">{event.location}</span>
          </div>
        )}

        {/* Attendees */}
        {event.attendees.length > 0 && (
          <div className="flex items-center gap-1 mt-2">
            <Users className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
            <div className="flex -space-x-1">
              {displayedAttendees.map((attendee, idx) => (
                <AttendeeAvatar key={idx} attendee={attendee} />
              ))}
            </div>
            {remainingCount > 0 && (
              <span className="text-xs text-[var(--text-tertiary)] ml-1">+{remainingCount}</span>
            )}
          </div>
        )}

        {/* Related Emails Summary */}
        {relatedEmails.length > 0 && (
          <button
            onClick={onToggleExpand}
            className="flex items-center gap-1.5 mt-2 text-xs text-[var(--brand-primary)] hover:underline"
          >
            <Mail className="w-3.5 h-3.5" />
            <span>
              {relatedEmails.length} {relatedEmails.length === 1 ? 'Mail' : 'Mails'} verknüpft
            </span>
            {isExpanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
        )}
      </div>

      {/* Expanded: Related Emails */}
      {isExpanded && relatedEmails.length > 0 && (
        <div className="border-t border-[var(--border-default)] p-2 max-h-48 overflow-y-auto">
          {relatedEmails.map((email) => (
            <RelatedEmailItem
              key={email.messageId}
              email={email}
              onSelect={() => onSelectMail?.(email.messageId)}
              isSelected={selectedMessageId === email.messageId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Main Component ============

export default function MeetingPrepColumn({
  column,
  account,
  onSelectMail,
  selectedMessageId,
  isFrozen = false,
  isDragEnabled = true,
  onQuickAction,
  onAddTodo,
  onMessagesLoaded,
}: MeetingPrepColumnProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    // Initialize with today in the target timezone
    const now = new Date();
    const todayStr = getDateKeyInTimezone(now);
    return new Date(todayStr + 'T12:00:00');
  });
  const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(new Set());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Fetch meeting preps
  const { meetingPreps, prepsByDay, stats, isLoading, error, refetch } = useMeetingPreps(
    account?.id,
    7
  );

  // Sync mutation
  const syncMutation = useSyncCalendar();
  const linkEmailMutation = useLinkEmailToEvent();

  // Droppable for drag & drop email linking
  const { setNodeRef, isOver } = useDroppable({
    id: `meeting-prep-${column.id}`,
    data: { type: 'meeting-prep-column', columnId: column.id },
  });

  // Calculate event counts by day for mini calendar (using timezone-aware keys)
  const eventCountsByDay = useMemo(() => {
    const counts = new Map<string, number>();
    
    for (const prep of meetingPreps) {
      const eventDate = new Date(prep.event.start);
      const key = getDateKeyInTimezone(eventDate);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    
    return counts;
  }, [meetingPreps]);

  // Filter events for selected day (timezone-aware)
  const displayedPreps = useMemo(() => {
    const selectedKey = getDateKeyInTimezone(selectedDate);
    
    return meetingPreps.filter((prep) => {
      const eventDate = new Date(prep.event.start);
      const eventKey = getDateKeyInTimezone(eventDate);
      return eventKey === selectedKey;
    });
  }, [meetingPreps, selectedDate]);

  // Notify parent about loaded message IDs
  useMemo(() => {
    const messageIds = meetingPreps.flatMap((p) => p.relatedEmails.map((e) => e.messageId));
    onMessagesLoaded?.(messageIds);
  }, [meetingPreps, onMessagesLoaded]);

  const toggleExpand = useCallback((eventId: string) => {
    setExpandedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  const handleSync = async () => {
    if (!account) return;
    try {
      await syncMutation.mutateAsync(account.id);
      toast.success('Kalender synchronisiert');
      refetch();
    } catch (err) {
      toast.error('Sync fehlgeschlagen');
    }
  };

  // Mock folders for settings (TODO: fetch from backend)
  const availableFolders = [
    { id: 'inbox', name: 'inbox', displayName: 'Posteingang' },
    { id: 'sent', name: 'sent', displayName: 'Gesendet' },
    { id: 'archive', name: 'archive', displayName: 'Archiv' },
    { id: 'drafts', name: 'drafts', displayName: 'Entwürfe' },
  ];

  if (!account) {
    return (
      <div
        className={cn(
          'flex flex-col w-[320px] min-w-[320px] rounded-lg',
          'bg-[var(--bg-secondary)] border border-[var(--border-default)]'
        )}
      >
        <div className="p-3 border-b border-[var(--border-default)]">
          <h2 className="font-medium text-[var(--text-primary)] flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {column.displayName || 'Meeting Prep'}
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-4 text-[var(--text-tertiary)] text-sm">
          Kein Mail-Account verbunden
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col w-[320px] min-w-[320px] rounded-lg transition-colors',
        'bg-[var(--bg-secondary)] border',
        isOver ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/5' : 'border-[var(--border-default)]',
        isFrozen && 'sticky left-0 z-10'
      )}
    >
      {/* Header */}
      <div className="p-3 border-b border-[var(--border-default)]">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-[var(--text-primary)] flex items-center gap-2">
            <span className="text-lg">{column.icon || '📅'}</span>
            {column.displayName || 'Meeting Prep'}
          </h2>
          <div className="flex items-center gap-1">
            {/* Settings Button */}
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="p-1.5 rounded hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]"
              title="Einstellungen"
            >
              <Settings className="w-4 h-4" />
            </button>
            {/* Sync Button */}
            <button
              onClick={handleSync}
              disabled={syncMutation.isPending}
              className="p-1.5 rounded hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)] disabled:opacity-50"
              title="Kalender synchronisieren"
            >
              <RefreshCw
                className={cn('w-4 h-4', syncMutation.isPending && 'animate-spin')}
              />
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats.total > 0 && (
          <div className="flex items-center gap-2 mt-2 text-xs text-[var(--text-secondary)]">
            {stats.hasUrgent && (
              <span className="px-2 py-0.5 rounded bg-orange-500/10 text-orange-500">
                {stats.soonCount} bald
              </span>
            )}
            <span>
              {stats.total} {stats.total === 1 ? 'Termin' : 'Termine'}
            </span>
            {stats.totalEmails > 0 && (
              <span className="text-[var(--text-tertiary)]">
                • {stats.totalEmails} Mails
              </span>
            )}
          </div>
        )}
      </div>

      {/* Mini Calendar */}
      <div className="border-b border-[var(--border-default)]">
        <MiniCalendarHeader
          currentDate={selectedDate}
          onDateChange={setSelectedDate}
          eventCountsByDay={eventCountsByDay}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--text-tertiary)]" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
            <p className="text-sm text-[var(--text-secondary)]">
              Kalender konnte nicht geladen werden
            </p>
            <button
              onClick={() => refetch()}
              className="mt-2 text-xs text-[var(--brand-primary)] hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {!isLoading && !error && displayedPreps.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Calendar className="w-8 h-8 text-[var(--text-tertiary)] mb-2" />
            <p className="text-sm text-[var(--text-secondary)]">
              Keine Termine an diesem Tag
            </p>
          </div>
        )}

        {!isLoading &&
          !error &&
          displayedPreps.map((prep) => (
            <EventCard
              key={prep.event.id}
              event={prep.event}
              relatedEmails={prep.relatedEmails}
              isExpanded={expandedEventIds.has(prep.event.id)}
              onToggleExpand={() => toggleExpand(prep.event.id)}
              onSelectMail={onSelectMail}
              selectedMessageId={selectedMessageId}
              accountId={account.id}
            />
          ))}
      </div>

      {/* Drop Zone Indicator */}
      {isOver && (
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--brand-primary)]/10 border-2 border-dashed border-[var(--brand-primary)] rounded-lg pointer-events-none z-20">
          <div className="bg-[var(--bg-primary)] px-4 py-2 rounded-lg shadow-lg">
            <span className="text-sm font-medium text-[var(--brand-primary)]">
              Mit Termin verknüpfen
            </span>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <MeetingPrepSettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        accountId={account.id}
        availableFolders={availableFolders}
      />
    </div>
  );
}
